import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import Currency from '@spotahome/soyuz-currency';

import { usePricesStatisticsContext } from '../../context/PricesStatisticsContext';

import {
  generateNoscriptTableHtml,
  getPropertyTypeSections
} from '../../utils/seoCharts';

import { SECTION_PROPERTY_TYPE_LABEL_MAP } from '../../constants/seoCharts';

import AreaChart from './AreaChart';
import ChartContainer from './ChartContainer';

import Table from './Table';
import NoDataAvailable from './NoDataAvailable';

const getTranslationKeys = (propertyType, statsLength) => {
  if (propertyType === 'all' || propertyType === null || statsLength === 0) {
    return {
      titleKey: 'chart.prices.title',
      subtitleKey: 'chart.prices.subtitle.plain',
      summaryKey: 'chart.prices.summary.plain'
    };
  }
  return {
    titleKey: 'chart.prices.property.type.title',
    subtitleKey: 'chart.prices.property.type.subtitle.plain',
    summaryKey: 'chart.prices.property.type.summary.plain'
  };
};

const PricingChartContainer = ({ location, isMobile = false }) => {
  const {
    pricesStatistics: stats,
    initialPropertyType,
    propertyType,
    setPropertyType,
    priceAverage,
    lastMonthDisplayed,
    currencyIsoCode,
    loading
  } = usePricesStatisticsContext();

  const sections = getPropertyTypeSections();

  const handleTabChange = tabId => {
    const newPropertyType = sections[tabId].id;
    setPropertyType(newPropertyType);
  };

  const activeIndex = propertyType
    ? sections.findIndex(item => item.id === propertyType)
    : 0;

  const translationsKeys = getTranslationKeys(propertyType, stats.length);
  const propertyTypeTranslation = propertyType
    ? trans(SECTION_PROPERTY_TYPE_LABEL_MAP[propertyType]).toLowerCase()
    : '';

  const formatValue = value =>
    Currency.formatPrice(parseFloat(value).toFixed(1), currencyIsoCode);

  if (!loading && initialPropertyType && stats.length === 0) {
    return null;
  }

  const renderChart = () => {
    if (!loading && stats.length === 0) {
      return <NoDataAvailable />;
    }
    return (
      <AreaChart data={stats} loading={loading} valueFormatter={formatValue} />
    );
  };

  const renderTable = () => {
    if (!loading && stats.length === 0) {
      return <NoDataAvailable />;
    }
    return (
      <Table
        data={stats}
        valueKey="averagePrice"
        labelKeys={['chart.table.month', 'chart.table.average.price']}
        formatValue={formatValue}
      />
    );
  };

  const title = trans(translationsKeys.titleKey, {
    city: location.city,
    propertyType: propertyTypeTranslation
  });

  const chartAriaLabel =
    !loading && stats.length > 0
      ? `${title}: ${stats.map(s => `${s.label} ${formatValue(s.averagePrice)}`).join(', ')}`
      : title;

  const noscriptContent =
    stats.length > 0
      ? generateNoscriptTableHtml({
          title,
          headers: [
            trans('chart.table.month'),
            trans('chart.table.average.price')
          ],
          rows: stats.map(s => [s.label, formatValue(s.averagePrice), s.delta])
        })
      : '';

  return (
    <ChartContainer
      id="pricing-chart"
      location={location}
      isMobile={isMobile}
      title={title}
      chartAriaLabel={chartAriaLabel}
      noscriptContent={noscriptContent}
      subtitle={trans(translationsKeys.subtitleKey, {
        city: location.city,
        country: trans(`country.${location.country}`),
        averagePrice: Currency.formatPrice(priceAverage, currencyIsoCode),
        propertyType: propertyTypeTranslation
      })}
      summary={trans(translationsKeys.summaryKey, {
        city: location.city,
        percentage: lastMonthDisplayed?.delta,
        lastMonthDisplayed: lastMonthDisplayed?.monthName,
        year: lastMonthDisplayed?.year,
        priceLastMonthDisplayed: Currency.formatPrice(
          lastMonthDisplayed?.averagePrice,
          currencyIsoCode
        ),
        propertyType: propertyTypeTranslation
      })}
      loading={loading}
      activeIndex={activeIndex}
      onTabChange={handleTabChange}
      sections={initialPropertyType ? [] : sections}
      renderChart={renderChart}
      renderTable={renderTable}
    />
  );
};

export default PricingChartContainer;

PricingChartContainer.propTypes = {
  isMobile: PropTypes.bool,
  location: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string
  }).isRequired
};
